<script setup lang="ts">
const userStore = useUserStore()
const modalStore = useModalStore()
const localePath = useLocalePath()
</script>

<template>
  <div class="min-h-screen bg-gradient-to-b from-gray-100 to-white">
    <!-- Hero Section -->

    <!-- Main Content -->
    <div class="max-w-7xl mx-auto px-4 xl:px-6 py-8">
      <!-- Three Modes Preview Cards -->
      <div class="mb-12 lg:mb-16">
        <h2 class="text-2xl xl:text-3xl font-bold text-poivre-900 text-center mb-8">
          Trois modes pour économiser
        </h2>
        <div class="grid grid-cols-1 md:grid-cols-3 gap-6">
          <!-- Mode 1: Flyers -->
          <div class="group relative p-4 md:p-6 text-left border-0 border-gray-200 rounded-xl hover:border-primary hover:shadow-lg transition-all cursor-default bg-sauge-100 flex md:block gap-4">
            <div class="flex">
              <div class="flex items-center md:mb-4">
                <div class="p-3 bg-sauge/10 rounded-lg group-hover:bg-sauge/20 transition-colors flex items-center justify-center">
                  <UIcon name="i-gridicons-tag" class="size-9 text-sauge" />
                </div>
              </div>
            </div>
            <div>
              <h3 class="text-lg font-bold text-poivre-900 mb-2">
                À partir des circulaires
              </h3>
              <p class="text-base text-poivre-600 leading-relaxed flex-grow">
                Trouver des recettes qui combinent les rabais les plus avantageux des circulaires de vos épiceries favorites.
              </p>
            </div>
          </div>

          <!-- Mode 2: Grocery List -->
          <div class="group relative p-4 md:p-6 text-left bg-sorbet-100 border-0 border-gray-200 rounded-xl hover:border-primary hover:shadow-lg transition-all cursor-default flex md:block gap-4">
            <div class="flex">
              <div class="flex items-center md:mb-4">
                <div class="p-3 bg-primary/10 rounded-lg group-hover:bg-primary/20 transition-colors flex items-center justify-center">
                  <UIcon name="i-material-symbols-light-list-alt-rounded" class="size-9 text-primary" />
                </div>
              </div>
            </div>
            <div>
              <h3 class="text-lg font-bold text-poivre-900 mb-2">
                À partir de votre liste d'épicerie
              </h3>
              <p class="text-base text-poivre-600 leading-relaxed flex-grow">
                Trouver des recettes à partir des rabais ajoutés à ma liste d'épicerie.
              </p>
            </div>
          </div>

          <!-- Mode 3: Specific Items -->
          <div class="group relative p-4 md:p-6 text-left border-0 border-gray-200 rounded-xl hover:border-primary hover:shadow-lg transition-all cursor-default bg-glace-100 flex md:block gap-4">
            <div class="flex">
              <div class="flex items-center md:mb-4">
                <div class="p-3 bg-bleuet/5 rounded-lg group-hover:bg-bleuet/10 transition-colors flex items-center justify-center">
                  <UIcon name="i-mdi-store-search" class="size-9 text-bleuet" />
                </div>
              </div>
            </div>
            <div>
              <h3 class="text-lg font-bold text-poivre-900 mb-2">
                À partir de rabais sélectionnés
              </h3>
              <p class="text-base text-poivre-600 leading-relaxed flex-grow">
                Trouver des recettes à partir d'un ou plusieurs rabais spécifiques de votre choix.
              </p>
            </div>
          </div>
        </div>
      </div>

      <!-- How It Works Section -->
      <div class="mb-12 lg:mb-16">
        <h2 class="text-2xl xl:text-3xl font-bold text-poivre-900 mb-8 text-center">
          Comment ça fonctionne?
        </h2>
        <div class="bg-white rounded-2xl p-8 lg:p-10 shadow-sm border border-gray-200">
          <ul class="space-y-6">
            <li class="flex items-center">
              <div class="flex-shrink-0 mr-4">
                <div class="flex items-center justify-center h-10 w-10 rounded-lg bg-tomate-100">
                  <UIcon name="i-mdi-check-circle" class="text-tomate-600 size-6" />
                </div>
              </div>
              <span class="text-lg text-poivre-700">Glouton analyse chaque semaine les circulaires des supermarchés</span>
            </li>
            <li class="flex items-center">
              <div class="flex-shrink-0 mr-4">
                <div class="flex items-center justify-center h-10 w-10 rounded-lg bg-tomate-100">
                  <UIcon name="i-mdi-check-circle" class="text-tomate-600 size-6" />
                </div>
              </div>
              <span class="text-lg text-poivre-700">Un score est attribué à chaque article selon sa saisonnalité, rareté et qualité</span>
            </li>
            <li class="flex items-center">
              <div class="flex-shrink-0 mr-4">
                <div class="flex items-center justify-center h-10 w-10 rounded-lg bg-tomate-100">
                  <UIcon name="i-mdi-check-circle" class="text-tomate-600 size-6" />
                </div>
              </div>
              <span class="text-lg text-poivre-700">Les recettes proposées sont les plus avantageuses selon les spéciaux actuels</span>
            </li>
            <li class="flex items-center">
              <div class="flex-shrink-0 mr-4">
                <div class="flex items-center justify-center h-10 w-10 rounded-lg bg-tomate-100">
                  <UIcon name="i-mdi-check-circle" class="text-tomate-600 size-6" />
                </div>
              </div>
              <span class="text-lg text-poivre-700">Les recommandations sont personnalisées selon vos épiceries préférées</span>
            </li>
          </ul>
        </div>
      </div>

      <!-- Benefits Section -->
      <div class="mb-12 lg:mb-16">
        <h2 class="text-2xl xl:text-3xl font-bold text-poivre-900 mb-8 text-center">
          Pourquoi utiliser L'économiseur?
        </h2>
        <div class="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div class="bg-gradient-to-br from-tomate-50 to-tomate-100 rounded-2xl p-8 border border-tomate-200">
            <div class="flex items-start">
              <UIcon name="i-mdi-star" class="text-tomate-600 size-7 flex-shrink-0 mr-4 mt-1" />
              <div>
                <h3 class="text-xl font-bold text-poivre-900 mb-2">
                  Économisez jusqu'à 150$ par mois
                </h3>
                <p class="text-poivre-700">
                  En visitant vos supermarchés habituels, maximisez vos économies
                </p>
              </div>
            </div>
          </div>

          <div class="bg-gradient-to-br from-sauge-50 to-sauge-100 rounded-2xl p-8 border border-sauge-200">
            <div class="flex items-start">
              <UIcon name="i-mdi-star" class="text-sauge-600 size-7 flex-shrink-0 mr-4 mt-1" />
              <div>
                <h3 class="text-xl font-bold text-poivre-900 mb-2">
                  Mangez plus varié
                </h3>
                <p class="text-poivre-700">
                  Sortez de votre routine culinaire et découvrez de nouvelles saveurs
                </p>
              </div>
            </div>
          </div>

          <div class="bg-gradient-to-br from-sorbet-50 to-sorbet-100 rounded-2xl p-8 border border-sorbet-200">
            <div class="flex items-start">
              <UIcon name="i-mdi-star" class="text-sorbet-600 size-7 flex-shrink-0 mr-4 mt-1" />
              <div>
                <h3 class="text-xl font-bold text-poivre-900 mb-2">
                  Profitez des spéciaux
                </h3>
                <p class="text-poivre-700">
                  Maximisez le potentiel de vos achats en solde
                </p>
              </div>
            </div>
          </div>

          <div class="bg-gradient-to-br from-glace-50 to-glace-100 rounded-2xl p-8 border border-glace-200">
            <div class="flex items-start">
              <UIcon name="i-mdi-star" class="text-bleuet-600 size-7 flex-shrink-0 mr-4 mt-1" />
              <div>
                <h3 class="text-xl font-bold text-poivre-900 mb-2">
                  Cuisinez au meilleur prix
                </h3>
                <p class="text-poivre-700">
                  Préparez vos recettes quand les ingrédients sont les plus avantageux
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>

      <!-- Testimonial Section -->
      <div class="mb-12 lg:mb-16">
        <div class="bg-gradient-to-r from-tomate-50 via-sorbet-50 to-sauge-50 rounded-2xl p-8 lg:p-12 border border-tomate-200/50 shadow-sm">
          <div class="flex items-center mb-6">
            <img src="/images/testimonials/lucie.jpg" alt="Lucie L." class="w-16 h-16 rounded-full mr-4 object-cover border-2 border-tomate-300">
            <div>
              <h3 class="text-xl font-bold text-poivre-800">
                <span class="text-tomate-600">Lucie L.</span>, Québec
              </h3>
              <div class="flex items-center gap-1 mt-1">
                <UIcon v-for="i in 5" :key="i" name="i-mdi-star" class="text-tomate-500 size-4" />
              </div>
              <p class="text-sm text-poivre-600 mt-1">
                Utilisatrice satisfaite
              </p>
            </div>
          </div>
          <blockquote class="text-lg lg:text-xl text-poivre-700 italic font-medium leading-relaxed">
            «En visitant les 2 mêmes supermarchés qu'à l'habitude, je réussis à économiser environ 150$ chaque mois et à manger plus varié en utilisant Glouton.»
          </blockquote>
        </div>
      </div>

      <!-- Call to Action Section -->
      <div class="text-center bg-gradient-to-r from-primary-50 to-primary-100 rounded-2xl p-8 lg:p-12 border border-primary-200 shadow-sm">
        <div class="flex justify-center mb-6">
          <UIcon name="i-mdi-star" class="text-tomate-500 size-12" />
        </div>
        <h2 class="text-3xl lg:text-4xl font-bold text-poivre-900 mb-4">
          Prêt à économiser?
        </h2>
        <p class="text-lg text-poivre-700 mb-8 max-w-3xl mx-auto leading-relaxed">
          Un compte avec <span class="font-bold text-tomate-600">abonnement Extra</span> est requis pour profiter de cet outil. Profitez de résultats illimités, de filtres avancés (catégories ou restrictions alimentaires) et bien plus encore. Avec les économies réalisées, il y a fort à parier que votre abonnement se remboursera tout seul!
        </p>

        <div v-if="!userStore.user" class="flex flex-col sm:flex-row gap-4 justify-center">
          <UButton
            color="primary"
            size="xl"
            icon="i-mdi-login-variant"
            @click="modalStore.openLoginModal()"
          >
            Se connecter
          </UButton>
          <UButton
            color="secondary"
            size="xl"
            :to="localePath({ name: 'RegisterPage' })"
          >
            Créer un compte
          </UButton>
        </div>

        <div v-else-if="userStore.user && !userStore.user.extra" class="flex flex-col sm:flex-row gap-4 justify-center">
          <UButton
            color="primary"
            size="xl"
            icon="i-mdi-star"
            to="/extra"
          >
            Découvrir l'Extra
          </UButton>
        </div>

        <div v-else class="inline-flex items-center gap-3 bg-white rounded-xl px-6 py-3 shadow-sm border border-green-200">
          <UIcon name="i-mdi-check-circle" class="text-green-600 size-10" />
          <span class="text-base font-medium text-poivre-700 leading-5">Vous êtes membre Extra! Commencez à économiser dès maintenant.</span>
        </div>
      </div>
    </div>
  </div>
</template>
